import { INLINE_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { TermsFooterSections } from './TermsFooterSections';

import type { ReactElement } from 'react';

/**
 * Terms of Service (`/terms`). Public-facing summary of the Master
 * Subscription Agreement (`docs/legal/msa-template.md`).
 *
 * **Scope discipline**: this page is the plain-language projection of
 * the MSA — the binding instrument is the MSA itself, delivered at
 * signup / on request and counter-signed (or click-accepted for
 * self-service). Per the same quality bar the DPA page follows, this
 * page links ONLY to routes that exist; capabilities the MSA names but
 * which are not yet published as their own pages (SLA, standalone AUP,
 * support policy) are described here and fulfilled via admin@syncanix.com
 * rather than linked to a dead URL. Tier-specific commercials are NOT
 * enumerated here (they live at /pricing, the single source of truth) so
 * this page never drifts from the price list.
 *
 * **Counsel gate**: like the DPA + AI pages, the binding MSA is pending
 * EU + commercial-contract counsel sign-off (launch-blocking). This page
 * states the standard terms; the executed agreement governs.
 *
 * **Three-layer contract preserved**: pure presentational. No data fetch.
 */
export function Terms(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('terms.meta.title'),
    description: t('terms.meta.description'),
    path: '/terms',
  });
  return (
    <LegalPage
      breadcrumb={t('terms.breadcrumb')}
      title={t('terms.title')}
      intro={
        <>
          {t('terms.intro.before')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>{' '}
          {t('terms.intro.after')}
        </>
      }
      lastUpdatedSlug="terms"
    >
      <div
        role="note"
        className="mb-10 rounded-md border border-warning/40 bg-warning/10 p-4 text-sm text-warning-foreground"
      >
        <strong>{t('terms.status.label')}</strong> {t('terms.status.body')}
      </div>

      <section aria-labelledby="terms-acceptance" className="mb-10">
        <h2
          id="terms-acceptance"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.acceptance.heading')}
        </h2>
        <p>{t('terms.acceptance.body')}</p>
      </section>

      <section aria-labelledby="terms-service" className="mb-10">
        <h2
          id="terms-service"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.service.heading')}
        </h2>
        <p className="mb-3">{t('terms.service.p1')}</p>
        <p>{t('terms.service.p2')}</p>
      </section>

      <section aria-labelledby="terms-accounts" className="mb-10">
        <h2
          id="terms-accounts"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.accounts.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('terms.accounts.item1')}</li>
          <li>{t('terms.accounts.item2')}</li>
          <li>{t('terms.accounts.item3')}</li>
        </ul>
      </section>

      <section aria-labelledby="terms-fees" className="mb-10">
        <h2
          id="terms-fees"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.fees.heading')}
        </h2>
        <ul className="list-disc space-y-2 ps-6">
          <li>
            <strong>{t('terms.fees.pricing.label')}</strong> {t('terms.fees.pricing.before')}{' '}
            <Link className={INLINE_LINK} to="/pricing">
              {/* A path as the LINK LABEL, so `isolatePaths` at the prose
                  renderer never sees it. Same bidi hazard either way: the
                  leading slash is neutral, so in Arabic and Hebrew it lands on
                  the far side of the word and the reader is shown `pricing/`. */}
              <bdi>/pricing</bdi>
            </Link>{' '}
            {t('terms.fees.pricing.after')}
          </li>
          <li>
            <strong>{t('terms.fees.cadence.label')}</strong> {t('terms.fees.cadence.body')}
          </li>
          <li>
            <strong>{t('terms.fees.noIncrease.label')}</strong> {t('terms.fees.noIncrease.before')}{' '}
            <Link className={INLINE_LINK} to="/pricing/history">
              {t('terms.fees.noIncrease.linkText')}
            </Link>
            {t('terms.fees.noIncrease.after')}
          </li>
          <li>
            <strong>{t('terms.fees.refunds.label')}</strong> {t('terms.fees.refunds.body')}
          </li>
          <li>
            <strong>{t('terms.fees.taxes.label')}</strong> {t('terms.fees.taxes.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="terms-data" className="mb-10">
        <h2
          id="terms-data"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.data.heading')}
        </h2>
        <p className="mb-3">
          {t('terms.data.p1.s1')} <strong>{t('terms.data.p1.ownsBold')}</strong>{' '}
          {t('terms.data.p1.s2')} <strong>{t('terms.data.p1.noTrainBold')}</strong>{' '}
          {t('terms.data.p1.s3')}{' '}
          <Link className={INLINE_LINK} to="/trust/dpa">
            {t('terms.data.p1.dpaLink')}
          </Link>
          {t('terms.data.p1.s4')}{' '}
          <Link className={INLINE_LINK} to="/trust/privacy">
            {t('terms.data.p1.privacyLink')}
          </Link>
          {t('terms.data.p1.s5')}
        </p>
      </section>

      <section aria-labelledby="terms-ai" className="mb-10">
        <h2
          id="terms-ai"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.ai.heading')}
        </h2>
        <ul className="list-disc space-y-2 ps-6">
          <li>
            <strong>{t('terms.ai.probabilistic.label')}</strong> {t('terms.ai.probabilistic.body')}
          </li>
          <li>
            <strong>{t('terms.ai.hitl.label')}</strong> {t('terms.ai.hitl.body')}
          </li>
          <li>
            <strong>{t('terms.ai.ownership.label')}</strong> {t('terms.ai.ownership.before')}{' '}
            <Link className={INLINE_LINK} to="/trust/ai">
              {t('terms.ai.ownership.linkText')}
            </Link>
            {t('terms.ai.ownership.after')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="terms-aup" className="mb-10">
        <h2
          id="terms-aup"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.aup.heading')}
        </h2>
        <p className="mb-3">
          {t('terms.aup.before')}{' '}
          <Link className={INLINE_LINK} to="/trust/ai">
            {t('terms.aup.linkText')}
          </Link>
          {t('terms.aup.after')}
        </p>
      </section>

      <section aria-labelledby="terms-sla" className="mb-10">
        <h2
          id="terms-sla"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.sla.heading')}
        </h2>
        <p>
          {t('terms.sla.before')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>
          {t('terms.sla.after')}
        </p>
      </section>

      <section aria-labelledby="terms-warranty" className="mb-10">
        <h2
          id="terms-warranty"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.warranty.heading')}
        </h2>
        <p>
          {t('terms.warranty.before')}{' '}
          <Link className={INLINE_LINK} to="/trust/security">
            {t('terms.warranty.linkText')}
          </Link>
          {t('terms.warranty.after')}
        </p>
      </section>

      <section aria-labelledby="terms-liability" className="mb-10">
        <h2
          id="terms-liability"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.liability.heading')}
        </h2>
        <p>
          {t('terms.liability.before')} <strong>{t('terms.liability.notBold')}</strong>{' '}
          {t('terms.liability.after')}
        </p>
      </section>

      <section aria-labelledby="terms-indemnity" className="mb-10">
        <h2
          id="terms-indemnity"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.indemnity.heading')}
        </h2>
        <p>{t('terms.indemnity.body')}</p>
      </section>

      <section aria-labelledby="terms-term" className="mb-10">
        <h2
          id="terms-term"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.term.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <strong>{t('terms.term.renewal.label')}</strong> {t('terms.term.renewal.body')}
          </li>
          <li>
            <strong>{t('terms.term.convenience.label')}</strong> {t('terms.term.convenience.body')}
          </li>
          <li>
            <strong>{t('terms.term.cause.label')}</strong> {t('terms.term.cause.body')}
          </li>
          <li>
            <strong>{t('terms.term.export.label')}</strong> {t('terms.term.export.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="terms-changes" className="mb-10">
        <h2
          id="terms-changes"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.changes.heading')}
        </h2>
        <p>{t('terms.changes.body')}</p>
      </section>

      <section aria-labelledby="terms-law" className="mb-10">
        <h2
          id="terms-law"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('terms.law.heading')}
        </h2>
        <p>{t('terms.law.body')}</p>
      </section>

      <TermsFooterSections />
    </LegalPage>
  );
}
